import { BlockList, PlacementProvider } from '@zillow/cms-blocks';
import { Box, Divider } from '@zillow/constellation';
import { AISummary } from '@zillow/zrs-cms';
import { format } from 'date-fns';
import React from 'react';

import FooterTemplate from '../FooterTemplate/FooterTemplate';
import RelatedPosts from '../RelatedPosts/RelatedPosts';
import Tags from '../Tags/Tags';

import {
    B2CPostTemplateProps,
    SidebarWrapperProps,
} from './B2CPostTemplate.types';
import type { Contributor } from './B2CPostTemplate.types';
import ContributorsCard from './ContributorsCard/ContributorsCard/ContributorsCard';
import PostFeaturedImage from './PostFeaturedImage/PostFeaturedImage';
import PostHeader from './PostHeader/PostHeader';

export const SidebarWrapper: React.FC<SidebarWrapperProps> = ({
    children,
    sticky = false,
    top = '$spacing.md',
    gapSpacing = '$spacing.xxl',
}) => (
    <Box
        sx={{
            ...(sticky
                ? {
                      position: 'sticky',
                      top: top,
                      alignSelf: 'flex-start',
                  }
                : {}),
            flexShrink: 0,
            marginTop: '$spacing.xl',
            marginLeft: 'auto',
            marginRight: 'auto',
            width: '300px',
            '@media $md_lte': {
                width: '100%',
                marginTop: 0,
            },
            '@media $lg': {
                marginLeft: gapSpacing,
                marginRight: 0,
                marginTop: 0,
            },
        }}
    >
        {children}
    </Box>
);

const B2CPostTemplate: React.FC<B2CPostTemplateProps> = ({
    post,
    contentWidth = '$breakpoints.xl',
    gapSpacing = '$spacing.xxl',
    stickySidebarTop,
}) => {
    const postSidebar = post?.sidebar ? (
        <BlockList blocks={post.sidebar.blocks || []} />
    ) : null;

    const reviewersArr: Contributor[] = Array.isArray(post.meta?.reviewedBy)
        ? post.meta.reviewedBy
              .filter(
                  (r): r is Contributor =>
                      r != null && typeof r.name === 'string'
              )
              .map((r: Contributor) => ({
                  name: r.name,
                  avatar: r.avatar,
                  link: r.link,
                  role: 'reviewer' as const,
                  jobTitle: r.jobTitle,
                  expertise: r.expertise,
                  description: r.description,
              }))
        : [];
    const editorsArr: Contributor[] = Array.isArray(post.meta?.editor)
        ? post.meta.editor.map((e: Contributor) => ({
              name: e.name,
              avatar: e.avatar,
              link: e.link,
              role: 'editor' as const,
              jobTitle: e.jobTitle,
              expertise: e.expertise,
              description: e.description,
          }))
        : [];

    const contributors = {
        author: post.author && {
            name: post.author.name,
            avatar: post.author.avatar,
            link: post.author.link,
            role: 'author' as const,
            date: post.date
                ? (() => {
                      try {
                          return format(new Date(post.date), 'MMMM d, yyyy');
                      } catch {
                          return undefined;
                      }
                  })()
                : undefined,
            jobTitle: post.author.jobTitle,
            expertise: post.author.expertise,
            description: post.author.description,
        },
        reviewers: reviewersArr,
        editors: editorsArr,
    };

    return (
        <React.Fragment>
            <Box
                sx={{
                    maxWidth: contentWidth,
                    margin: '0 auto',
                    padding: '$spacing.xl $spacing.sm',
                    paddingTop: '$spacing.md',
                    '@media $lg': {
                        paddingTop: '$spacing.lg',
                    },
                }}
            >
                {/* All content container, including sidebar */}
                <Box>
                    <PostHeader post={post} />

                    {/* This is the main content and sidebar */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            '@media $md_lte': {
                                flexDirection: 'column',
                                gap: '$spacing.md',
                            },
                        }}
                    >
                        {/* Main content container */}
                        <Box
                            data-epi-type="content"
                            sx={{
                                flex: '1 1 0',
                                minWidth: 0,
                                maxWidth: '100%',
                            }}
                        >
                            <PostFeaturedImage post={post} />
                            {contributors && contributors.author && (
                                <Box
                                    sx={{
                                        margin: '$spacing.sm 0 $spacing.md 0',
                                        width: '100%',
                                    }}
                                >
                                    <ContributorsCard
                                        author={contributors.author}
                                        reviewers={contributors.reviewers}
                                        editors={contributors.editors}
                                    />
                                </Box>
                            )}

                            {/* Divider */}
                            <Divider marginBottom="md" />

                            <AISummary
                                bullets={post.meta?.aiSummaryBullets}
                                paragraph={post.meta?.aiSummaryParagraph}
                                displayType={post.meta?.aiSummaryDisplayType}
                            />

                            <PlacementProvider placement="post">
                                <BlockList blocks={post.blocks || []} />
                            </PlacementProvider>
                            <Tags postTags={post.postTags} />
                        </Box>

                        {/* Sidebar container */}
                        {postSidebar && (
                            <SidebarWrapper
                                sticky
                                top={stickySidebarTop}
                                gapSpacing={gapSpacing}
                            >
                                {postSidebar}
                            </SidebarWrapper>
                        )}
                    </Box>
                </Box>
            </Box>

            {(post.footer || post.relatedPosts) && (
                <FooterTemplate
                    footer={post.footer}
                    relatedArticles={
                        post.relatedPosts && (
                            <RelatedPosts
                                post={{
                                    ...post,
                                    id: parseInt(post.id),
                                    relatedPosts: post.relatedPosts.map(
                                        (p) => ({
                                            ...p,
                                            id: parseInt(p.id),
                                        })
                                    ),
                                }}
                            />
                        )
                    }
                />
            )}
        </React.Fragment>
    );
};

export default B2CPostTemplate;
